import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import PMSteps from "../../../../components/utils/PMSteps";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import ChecklistItemLinesCompact from "../../../../components/dashboard/quality/checklists/checklistItemLinesCompact";
import { DocumentCenter } from "../../../../components/dashboard/documents/document-center";
import { createChecklistAsync } from "../../../../slices/quality/checklist";
import { fetchChecklistType } from "../../../../api/quality/checklistType";
import { fetchVehicle } from "../../../../api/vehicle";
import { dispatchWithToast } from "../../../../components/Utils";
import {
  ArrowPathRoundedSquareIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";

export const ChecklistCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    checklistType: null,
    vehicle: null,
  });

  const checklistTypeId = searchParams.get("checklist_type_id");
  const vehicleId = searchParams.get("vehicle_id");

  const [stages, setStages] = useState([
    {
      id: 1,
      title: t("Basic Information"),
      description: t("Enter checklist basic details"),
      current: true,
      completed: false,
      active: true,
    },
    {
      id: 2,
      title: t("Checklist Items"),
      description: t("Define checklist items and requirements"),
      current: false,
      completed: false,
      active: true,
    },
    {
      id: 3,
      title: t("Documents"),
      description: t("Upload required documents"),
      current: false,
      completed: false,
      active: true,
    },
    {
      id: 4,
      title: t("Review & Create"),
      description: t("Review and create the checklist"),
      current: false,
      completed: false,
      active: true,
    },
  ]);

  const [currentStageIdx, setCurrentStageIdx] = useState(0);
  const [stage, setStage] = useState(stages[0]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setItemState((prev) => ({ ...prev, isLoading: true }));

    try {
      const promises = [];

      if (checklistTypeId) {
        promises.push(fetchChecklistType(checklistTypeId));
      }

      if (vehicleId) {
        promises.push(fetchVehicle(vehicleId));
      }

      const results = await Promise.all(promises);

      setItemState((prev) => ({
        ...prev,
        isLoading: false,
        checklistType: results[0]?.data || null,
        vehicle: results[1]?.data || null,
      }));
    } catch (error) {
      console.error("Error loading initial data:", error);
      setItemState((prev) => ({
        ...prev,
        isLoading: false,
        error: true,
        errors: ["Failed to load initial data"],
      }));
    }
  };

  const handleStageChange = (newStage) => {
    const stageIndex = stages.findIndex((s) => s.id === newStage.id);
    setCurrentStageIdx(stageIndex);
    setStage(newStage);
  };

  const handleNextStage = async (formik) => {
    await formik.validateForm();

    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {}),
      true
    );

    if (!formik.isValid) {
      toast.error(t("Please fix the errors before continuing"));
      return;
    }

    if (currentStageIdx < stages.length - 1) {
      const nextStage = stages[currentStageIdx + 1];
      handleStageChange(nextStage);
    }
  };

  const getInitialValues = () => {
    const base = {
      checklist_type_id: checklistTypeId,
      vehicle_id: vehicleId,
    };

    switch (stage?.id) {
      case 1:
        return {
          ...base,
          title: "",
          description: "",
          date: new Date().toISOString().split("T")[0],
          responsible_executive_id: null,
          items: itemState.checklistType?.items || [],
        };
      case 2:
        return {
          ...base,
          items: itemState.checklistType?.items || [],
        };
      case 3:
        return {
          ...base,
          documents: [],
        };
      case 4:
        return {
          ...base,
          finished: false,
          approved: false,
        };
      default:
        return base;
    }
  };

  const validationSchemas = {
    1: Yup.object({
      title: Yup.string().required(t("Title is required")),
      description: Yup.string().required(t("Description is required")),
      date: Yup.string().required(t("Date is required")),
    }),
    2: Yup.object({
      items: Yup.array()
        .of(
          Yup.object({
            title: Yup.string().required(t("Item title is required")),
            description: Yup.string(),
          })
        )
        .min(1, t("At least one item is required")),
    }),
  };

  const handleSubmit = async (values, helpers) => {
    try {
      const payload = {
        ...values,
        company_id: 1, // This should come from user context
        project_id: null,
        customer_id: null,
        folder_id: null,
      };

      await dispatchWithToast(
        dispatch(createChecklistAsync(payload)),
        (data) => {
          toast.success(t("Checklist created successfully"));
          // Handle different response structures: data.id or data.data.id
          const checklistId = data?.id || data?.data?.id;
          if (checklistId) {
            navigate(`/dashboard/hse-ks/checklists/${checklistId}`);
          } else {
            console.error("Checklist ID not found in response:", data);
            toast.error(t("Checklist created but could not redirect"));
          }
        },
        () => {
          helpers.setSubmitting(false);
        }
      );

      helpers.setStatus({ success: true });
      helpers.setSubmitting(false);
    } catch (err) {
      toast.error(t("Something went wrong"));
      console.error(err);
      helpers.setStatus({ success: false });
      helpers.setErrors({ submit: err.message });
      helpers.setSubmitting(false);
    }
  };

  const renderStageContent = (formik) => {
    switch (stage?.id) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <PMField
                name="title"
                label={t("Title")}
                placeholder={t("Enter checklist title")}
                {...formik}
              />

              <PMField name="date" label={t("Date")} type="date" {...formik} />
            </div>

            <PMField
              name="description"
              label={t("Description")}
              placeholder={t("Enter checklist description")}
              multiline
              rows={4}
              {...formik}
            />

            {itemState.vehicle && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">
                  {t("Vehicle Information")}
                </h4>
                <p className="text-sm text-blue-700">
                  {itemState.vehicle.number_plate} - {itemState.vehicle.model}
                </p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <ChecklistItemLinesCompact
              checklist={itemState.checklistType}
              items={formik.values.items || []}
              editable={true}
              isLoading={false}
              drag={true}
              addRow={true}
              deleteRow={true}
              deviation={false}
              disabled={false}
              type="checklist"
              parentCallback={(data) => formik.setFieldValue("items", data)}
            />
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <DocumentCenter
              module="checklist"
              moduleParam="new"
              title={t("Checklist Documents")}
              subtitle={t("Upload documents related to this checklist")}
              canUpload={true}
              allowedFileTypes={["pdf", "doc", "docx", "jpg", "jpeg", "png"]}
            />
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                {t("Checklist Summary")}
              </h4>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Title")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formik.values.title}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Date")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formik.values.date}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Items Count")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formik.values.items?.length || 0}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t("Vehicle")}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {itemState.vehicle
                      ? `${itemState.vehicle.number_plate} - ${itemState.vehicle.model}`
                      : t("Not specified")}
                  </dd>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <PMSwitch
                name="finished"
                label={t("Mark as finished")}
                {...formik}
              />

              <PMSwitch
                name="approved"
                label={t("Mark as approved")}
                {...formik}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderContent = () => {
    if (itemState.isLoading) {
      return <ResourceLoading />;
    }

    if (itemState.error) {
      return <ResourceError errors={itemState.errors} />;
    }

    return (
      <div className="container bg-white px-4 py-4 rounded-xl">
        <div>
          <PMSteps
            steps={stages}
            selected={stage}
            onChange={handleStageChange}
          />
        </div>

        <div className="mt-8">
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchemas[stage?.id] || Yup.object()}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {(formik) => (
              <Form>
                {renderStageContent(formik)}

                <div className="mt-8 flex justify-between">
                  {currentStageIdx > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const prevStage = stages[currentStageIdx - 1];
                        handleStageChange(prevStage);
                      }}
                      className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                    >
                      {t("Previous")}
                    </button>
                  )}

                  <div className="flex gap-3">
                    {currentStageIdx < stages.length - 1 ? (
                      <button
                        type="button"
                        onClick={() => handleNextStage(formik)}
                        className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
                      >
                        {t("Next")}
                      </button>
                    ) : (
                      <button
                        type="submit"
                        disabled={formik.isSubmitting}
                        className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {formik.isSubmitting
                          ? t("Creating...")
                          : t("Create Checklist")}
                      </button>
                    )}
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    );
  };

  return (
    <>
      <DashboardHeader
        subtitle=""
        title={t("Create New Checklist")}
        meta={[{ label: "Checklist", icon: ArrowPathRoundedSquareIcon }]}
        mainButton={{
          label: "Back",
          action: () => navigate(-1),
          path: null,
          color: "",
          icon: PencilIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {renderContent()}
          </div>
        </div>
      </DashboardHeader>
    </>
  );
};
